<template>
  <div class="col">
    <b-table
      id="#my-leads-table"
      ref="myLeadsTable"
      class="b-table bg-white border overflow-hidden shadow table"
      hover
      striped
      show-empty
      :empty-text="$t('No leads matching your search criteria were found. Please adjust the filters and try again')"
      selectable
      :no-border-collapse="true"
      :items="leadsProvider"
      :fields="fields"
      :busy="busy"
      :per-page="pageSize"
      :current-page="page"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :no-local-sorting="true"
      :sort-icon-left="true"
      :no-sort-reset="true"
      @row-clicked="leadClick"
    >
      <template v-slot:cell(select)="data" class="first-col">
        <b-form-checkbox v-if="(data.item.dashboard_lead) ? !data.item.dashboard_lead.archived : true" v-model="archiveSelected" :value="data.item" />
      </template>
      <template v-slot:head(select)>
        <b-button class="font-weight-bold font-size-14 p-0 m-0" variant="link" @click="toggleSelect">
          {{ (archiveSelected.length === 0)? $t("Select All"): $t("Unselect All") }}
        </b-button>
      </template>
      <template v-slot:cell(statusIcons)="data">
        <lead-status-icons :status-icons="data.value" :archived="(data.item.dashboard_lead) ? data.item.dashboard_lead.archived : false" />
      </template>
      <template v-slot:cell(accepted_date)="data">
        {{ getReleaseDate(data.item) | parseDate }}
        <br>
        {{ getReleaseDate(data.item) | parseTime }}
      </template>
      <template v-slot:cell(timescale)="data">
        {{ data.value }}
      </template>
      <template v-slot:cell(feedbackRating)="data">
        <b-form-rating
          v-model="data.value"
          class="b-rating-feedback text-center p-0"
          no-border
          precision="2"
          readonly
          inline
        />
      </template>
      <template v-slot:cell(view)="data">
        <div class="d-flex">
          <a class="btn btn-sm btn-outline-secondary mr-3" :href="printUrl(data.item.code)" target="_blank">
            <font-awesome-icon icon="print" />
          </a>
          <router-link
            :to="{name:'my-lead', params: {code:data.item.code, fromPage: page}}"
            class="btn btn-sm btn-lead-view btn-outline-primary"
          >
            {{ $t('Full View') }}
          </router-link>
        </div>
      </template>
      <template v-slot:table-busy>
        <spinner :display-loading="true" />
      </template>
    </b-table>
    <div class="d-flex justify-content-between">
      <b-select v-if="items > 20" v-model="pageSize" :options="pageSizeOptions" class="w-auto page-size" />
      <b-pagination
        v-if="items/pageSize > 1"
        v-model="page"
        :total-rows="items"
        :per-page="pageSize"
        aria-controls="my-leads-table"
        align="right"
      />
    </div>
  </div>
</template>

<script>
import {MY_LEADS_RESULT_COUNT} from "@/store/mutation-types";
import Timescale from "@/imports/mixins/timescale";
import LeadStatusIcons from "@/components/list/LeadStatusIcons";
import Notification from '@/imports/mixins/notification';
import PrintUrl from '@/imports/mixins/printUrl';
import Spinner from "@/components/animation/Spinner";
import company from "@/imports/mixins/company";
import workTypeTranslation from "@/imports/mixins/workTypeTranslation";
import { captureError } from "@/services/userMonitoring";

export default {
  name: "MyLeadsTable",
  components: {LeadStatusIcons, Spinner},
  mixins:[Timescale, Notification, PrintUrl, company, workTypeTranslation],
  props:['fields'],
  data() {
    return {
      busy: false,
      items: 0,
      filterKey: 'myLeads',
      sortBy: 'accepted_date',
      sortDesc: true,
      archiveSelected: [],
      unarchivedLeads: [],
    }
  },
  computed: {
    filters: function() {
      return {...this.$store.getters.getFilter(this.filterKey), archived: this.$store.state.leads.SHOW_ARCHIVED_LEADS};
    },
    pagePreSelect: function() {
      return this.$route.params.pagination;
    },
    page: {
      get: function() {
        return this.$route.query.page ?? 1;
      },
      set: function(value) {
        if ((this.$route.query.page && this.$route.query.page !== value) || (this.$route.query.page === undefined && value !== 1)) {
          this.$router.push({name:'my-leads', query:{...this.$route.query, page:value}});
        }
      }
    },
    sortOrder: function() {
      return this.sortBy ? (this.sortBy + '-' + (this.sortDesc ? 'desc' : 'asc')) : null;
    },
    pageSize: {
      get: function() {
        return this.$store.state.MY_LEADS_RESULT_COUNT;
      },
      set: function(value) {
        this.$store.commit(MY_LEADS_RESULT_COUNT, value);
      }
    },
    pageSizeOptions: function() {
      return [
        {value: 10, text: this.$t('Show 10')},
        {value: 20, text: this.$t('Show 20')},
        {value: 40, text: this.$t('Show 40')},
        {value: 60, text: this.$t('Show 60')},
      ];
    }
  },
  methods: {
    leadsProvider: async function() {
      try {
        let result = await this.$api.leads.leads({...this.filters, page:this.page, order:this.sortOrder, pageSize:this.pageSize});
        this.setMeta(result.data.meta);
        this.$emit('updated', this.items);
        this.unarchivedLeads = result.data.data.filter(lead => (lead.dashboard_lead) ? !lead.dashboard_lead.archived : true);
        return result.data.data;
      } catch (e) {
        captureError(e, {
          section: "my-leads",
          action: "get-leads",
          type: "api-request"
        });
      }
    },
    getReleaseDate: function(lead) {
      return lead.accepted_date ?? lead.first_request;
    },
    setMeta: function(metaData) {
      this.items = metaData.totalResults;
      this.pageSize = metaData.pageSize;
    },
    telephone: function(value, key, item) {
      return item.enquiry.telephone;
    },
    postcode: function (value, key, item) {
      return item.enquiry.postcode;
    },
    workType: function (value, key, item) {
      let commsLanguage = this.company?.communications_language?.iso_code ?? 'en';
      if (commsLanguage === 'en') {
        return item.enquiry.work_type_description;
      }
      let translation = item.enquiry.work_type.translations.find(translation => translation.language.iso_code === commsLanguage);
      return translation ? translation.description : item.enquiry.work_type_description;
    },
    rating: function (value, key, item) {
      return item.feedbackRating / 2;
    },
    timescale: function (value, key, item) {
      return this.getTimescaleTranslation(item.enquiry.question_answers);
    },
    name: function (value, key, item) {
      return item.enquiry.title
        + ' ' + item.enquiry.forename
        + ' ' + item.enquiry.surname
    },
    leadClick: function(lead) {
      this.$router.push({name:'my-lead', params: {code:lead.code, fromPage: this.page}});
    },
    archive: function() {
      this.$api.leads.bulkArchive(this.archiveSelected.map(lead => lead.code)).then(() => {
        this.$emit('archived');
        this.archiveSelected = [];
        this.$refs.myLeadsTable.refresh();
      })
    },
    toggleSelect: function() {
      if (this.archiveSelected.length === 0) {
        this.archiveSelected = [...this.unarchivedLeads];
      } else {
        this.archiveSelected = [];
      }
    }
  },
  watch: {
    filters: function() {
      this.$refs.myLeadsTable.refresh()
    },
    archiveSelected: function(value) {
      this.$emit('archive', value.length > 0);
    }
  },
  created: function() {
    if (this.pagePreSelect) {
      this.page = this.pagePreSelect;
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep table {
  tbody {
    .first-col {
      padding-left: 30px;
    }
  }
  thead .first-col div {
    padding-left: 15px;
  }
}

</style>
